import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Clock, Play, Square, Settings, RefreshCw, ChevronDown, ChevronUp, History, Flame, Plus, Minus, CheckCircle, Trash2, Edit } from 'lucide-react';
import { sounds } from '../lib/sound';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export interface FastingState {
  isActive: boolean;
  isFasting: boolean;
  startTime: number; // millisecond timestamp
  durationHours: number; // target hours (e.g. 16 for fasting, 8 for eating)
  protocol: '16:8' | '18:6' | '20:4' | '14:10';
  history: Array<{
    id: string;
    startTime: number;
    endTime: number;
    protocol: string;
    isFastingPhase: boolean; // fast vs eating
    targetDuration: number;
    actualDuration: number; // in hours
    completed: boolean;
  }>;
}

interface FastingTimerProps {
  userId?: string | null;
  fastingState: FastingState | undefined;
  onSaveFastingState: (state: FastingState) => Promise<void>;
  lang: string;
  illumination?: boolean;
}

const LOCAL_STORAGE_KEY = 'main_nutrition_fasting_state';

const DEFAULT_STATE: FastingState = {
  isActive: false,
  isFasting: true,
  startTime: 0,
  durationHours: 16,
  protocol: '16:8',
  history: []
};

const PROTOCOLS = [
  { value: '16:8' as const, fast: 16, eat: 8, label: '16:8 (Standard)', descEn: '16h Fast / 8h Eating Window', descUa: '16г голодування / 8г їжі' },
  { value: '18:6' as const, fast: 18, eat: 6, label: '18:6 (Leangains)', descEn: '18h Fast / 6h Eating Window', descUa: '18г голодування / 6г їжі' },
  { value: '20:4' as const, fast: 20, eat: 4, label: '20:4 (Warrior)', descEn: '20h Fast / 4h Eating Window', descUa: '20г голодування / 4г їжі' },
  { value: '14:10' as const, fast: 14, eat: 10, label: '14:10 (Gentle)', descEn: '14h Fast / 10h Eating Window', descUa: '14г голодування / 10г їжі' },
];

const TRANSLATIONS: Record<string, any> = {
  'en': {
    title: 'Intermittent Fasting',
    fastingStatus: 'Fasting Window',
    eatingStatus: 'Eating Window',
    inactiveStatus: 'Timer Inactive',
    startFast: 'Start Fasting',
    endFast: 'End Fast & Start Eating',
    startEating: 'Start Eating Window',
    endEating: 'End Eating & Start Fasting',
    resetTimer: 'Reset Timer',
    protocol: 'Protocol',
    elapsed: 'Elapsed',
    remaining: 'Remaining',
    extraTime: 'Extra Fasting',
    completed: 'Goal Achieved!',
    fastingLogs: 'Fasting Logs',
    hours: 'h',
    minutes: 'm',
    seconds: 's',
    adjustStart: 'Adjust Start Time',
    back30m: '-30m',
    back1h: '-1h',
    forward30m: '+30m',
    forward1h: '+1h',
    noLogs: 'No fasting logs yet.',
    logFastingText: 'fast completed successfully',
    logEatingText: 'eating window ended',
    resetConfirm: 'Are you sure you want to reset the active fasting timer?',
    settings: 'Settings',
    showHistory: 'Fasting History',
    hideHistory: 'Hide History',
    target: 'Target',
    actual: 'Actual'
  },
  'ua': {
    title: 'Інтервальне голодування',
    fastingStatus: 'Період голодування',
    eatingStatus: 'Вікно прийому їжі',
    inactiveStatus: 'Таймер неактивний',
    startFast: 'Почати голодування',
    endFast: 'Завершити голодування',
    startEating: 'Почати їсти',
    endEating: 'Завершити вікно їжі',
    resetTimer: 'Скинути таймер',
    protocol: 'Протокол',
    elapsed: 'Минуло',
    remaining: 'Залишилось',
    extraTime: 'Понад норму',
    completed: 'Ціль досягнута!',
    fastingLogs: 'Історія голодування',
    hours: 'г',
    minutes: 'хв',
    seconds: 'с',
    adjustStart: 'Налаштувати час початку',
    back30m: '-30хв',
    back1h: '-1г',
    forward30m: '+30хв',
    forward1h: '+1г',
    noLogs: 'Немає записів голодування.',
    logFastingText: 'голодування успішно завершено',
    logEatingText: 'вікно прийому їжі закінчено',
    resetConfirm: 'Ви впевнені, що хочете скинути активний таймер голодування?',
    settings: 'Налаштування',
    showHistory: 'Історія голодування',
    hideHistory: 'Приховати історію',
    target: 'Ціль',
    actual: 'Факт'
  }
};

export const FastingTimer: React.FC<FastingTimerProps> = ({
  userId,
  fastingState,
  onSaveFastingState,
  lang,
  illumination = false
}) => {
  const t = lang.startsWith('ua') ? TRANSLATIONS['ua'] : TRANSLATIONS['en'];
  const [showHistory, setShowHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);


  
  // Local state initialized with prop or DEFAULT
  const state = useMemo<FastingState>(() => {
    return fastingState || DEFAULT_STATE;
  }, [fastingState]);

  // Keep track of current ticks for countdown
  const [now, setNow] = useState<number>(Date.now());

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (state.isActive) {
      interval = setInterval(() => {
        setNow(Date.now());
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [state.isActive]);

  // Calculations
  const elapsedMs = state.isActive && state.startTime ? Math.max(0, now - state.startTime) : 0;
  const targetMs = state.durationHours * 60 * 60 * 1000;
  const remainingMs = Math.max(0, targetMs - elapsedMs);
  const isOverTime = elapsedMs >= targetMs;
  const progressPercentage = state.isActive ? Math.min(100, (elapsedMs / targetMs) * 100) : 0;

  // Format milliseconds to HH:MM:SS
  const formatDuration = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const pad = (num: number) => String(num).padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  const formattedElapsed = formatDuration(elapsedMs);
  const formattedRemaining = formatDuration(remainingMs);
  const formattedOvertime = formatDuration(elapsedMs - targetMs);

  const currentProtocolDetail = PROTOCOLS.find(p => p.value === state.protocol) || PROTOCOLS[0];

  // Handlers
  const handleStartFasting = async () => {
    sounds.playPop();
    const targetHours = currentProtocolDetail.fast;
    const newState: FastingState = {
      ...state,
      isActive: true,
      isFasting: true,
      startTime: Date.now(),
      durationHours: targetHours,
      protocol: state.protocol
    };
    await onSaveFastingState(newState);
    toast.success(lang.startsWith('ua') ? 'Голодування розпочато! ⏱️' : 'Fasting started! ⏱️');
  };

  const handleStartEating = async () => {
    sounds.playSave();
    
    // Log completed fasting to history
    const actualDurationHours = elapsedMs / (1000 * 60 * 60);
    const completed = actualDurationHours >= state.durationHours;
    
    const historyEntry = {
      id: Math.random().toString(36).substring(2, 9) + Date.now().toString(36),
      startTime: state.startTime,
      endTime: Date.now(),
      protocol: state.protocol,
      isFastingPhase: true,
      targetDuration: state.durationHours,
      actualDuration: actualDurationHours,
      completed
    };

    const targetHours = currentProtocolDetail.eat;
    const newState: FastingState = {
      ...state,
      isActive: true,
      isFasting: false,
      startTime: Date.now(),
      durationHours: targetHours,
      history: [historyEntry, ...state.history]
    };

    await onSaveFastingState(newState);
    if (completed) {
      sounds.playSuccess();
      toast.success(lang.startsWith('ua') ? 'Чудова робота! Ціль голодування досягнута! 🏆' : 'Amazing job! Fasting target reached! 🏆');
    } else {
      toast.info(lang.startsWith('ua') ? 'Голодування завершено раніше. Почалось вікно їжі.' : 'Fast ended early. Eating window started.');
    }
  };

  const handleEndEatingAndStartFast = async () => {
    sounds.playSave();

    const actualDurationHours = elapsedMs / (1000 * 60 * 60);
    const completed = actualDurationHours <= state.durationHours; // eating window should not exceed limit
    
    const historyEntry = {
      id: Math.random().toString(36).substring(2, 9) + Date.now().toString(36),
      startTime: state.startTime,
      endTime: Date.now(),
      protocol: state.protocol,
      isFastingPhase: false,
      targetDuration: state.durationHours,
      actualDuration: actualDurationHours,
      completed
    };

    const targetHours = currentProtocolDetail.fast;
    const newState: FastingState = {
      ...state,
      isActive: true,
      isFasting: true,
      startTime: Date.now(),
      durationHours: targetHours,
      history: [historyEntry, ...state.history]
    };

    await onSaveFastingState(newState);
    toast.success(lang.startsWith('ua') ? 'Голодування розпочато! Успіхів! 💪' : 'Fasting window started! Stay strong! 💪');
  };

  const handleResetTimer = async () => {
    sounds.playPop();
    if (window.confirm(t.resetConfirm)) {
      const newState: FastingState = {
        ...state,
        isActive: false,
        startTime: 0
      };
      await onSaveFastingState(newState);
      toast.info(lang.startsWith('ua') ? 'Таймер скинуто' : 'Timer reset');
    }
  };

  const handleSelectProtocol = async (proto: '16:8' | '18:6' | '20:4' | '14:10') => {
    sounds.playPop();
    const detail = PROTOCOLS.find(p => p.value === proto) || PROTOCOLS[0];
    const targetHours = state.isFasting ? detail.fast : detail.eat;
    const newState: FastingState = {
      ...state,
      protocol: proto,
      durationHours: state.isActive ? targetHours : detail.fast
    };
    await onSaveFastingState(newState);
  };

  const handleAdjustTime = async (offsetMinutes: number) => {
    sounds.playPop();
    if (!state.isActive || !state.startTime) return;
    const newStartTime = state.startTime + (offsetMinutes * 60 * 1000);
    
    // Prevent starting in future
    if (newStartTime > Date.now()) {
      toast.error(lang.startsWith('ua') ? 'Час початку не може бути в майбутньому!' : 'Start time cannot be in the future!');
      return;
    }

    const newState: FastingState = {
      ...state,
      startTime: newStartTime
    };
    await onSaveFastingState(newState);
    toast.success(lang.startsWith('ua') ? 'Час початку скориговано' : 'Start time adjusted');
  };

  const handleDeleteHistoryLog = async (id: string) => {
    sounds.playPop();
    const updatedHistory = state.history.filter(item => item.id !== id);
    const newState: FastingState = {
      ...state,
      history: updatedHistory
    };
    await onSaveFastingState(newState);
    toast.success(lang.startsWith('ua') ? 'Запис видалено' : 'Log entry deleted');
  };

  // SVG Progress Arc Parameters
  const radius = 80;
  const strokeWidth = 10;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progressPercentage / 100) * circumference;

  return (
    <div className={cn(
      "bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-6 shadow-sm mt-4 relative overflow-hidden transition-all duration-300",
      illumination && "illumination-both"
    )}>
      {/* Background glow effects */}
      <div className="absolute -left-12 -top-12 w-32 h-32 bg-emerald-500/5 rounded-full blur-2xl pointer-events-none" />
      <div className="absolute -right-12 -bottom-12 w-32 h-32 bg-blue-500/5 rounded-full blur-2xl pointer-events-none" />

      {/* Header */}
      <div className="flex items-center justify-between mb-4 relative z-10">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-emerald-500 animate-pulse" />
          <h3 className="text-base font-extrabold text-[var(--text)] tracking-tight">
            {t.title}
          </h3>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 rounded-xl bg-[var(--input-bg)] text-zinc-500 hover:text-[var(--text)] transition-all hover:scale-105 active:scale-95"
            title={t.settings}
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="p-2 rounded-xl bg-[var(--input-bg)] text-zinc-500 hover:text-[var(--text)] transition-all hover:scale-105 active:scale-95 flex items-center gap-1 text-xs font-bold"
          >
            <History className="w-4 h-4" />
            <span className="hidden sm:inline">{showHistory ? t.hideHistory : t.showHistory}</span>
          </button>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center relative z-10">
        
        {/* SVG Circle Progress - Spans 5 columns on desktop */}
        <div className="md:col-span-5 flex flex-col items-center justify-center py-2">
          <div className="relative w-44 h-44 flex items-center justify-center">
            {/* SVG Progress Circle */}
            <svg className="w-full h-full transform -rotate-90">
              {/* Track */}
              <circle
                cx="88"
                cy="88"
                r={radius}
                className="stroke-zinc-100 dark:stroke-zinc-800"
                strokeWidth={strokeWidth}
                fill="transparent"
              />
              {/* Dynamic Progress Indicator */}
              <circle
                cx="88"
                cy="88"
                r={radius}
                className={cn(
                  "transition-all duration-1000 ease-out",
                  state.isActive 
                    ? state.isFasting 
                      ? "stroke-emerald-500" 
                      : "stroke-blue-500"
                    : "stroke-zinc-300 dark:stroke-zinc-700"
                )}
                strokeWidth={strokeWidth}
                fill="transparent"
                strokeDasharray={circumference}
                strokeDashoffset={state.isActive ? strokeDashoffset : circumference}
                strokeLinecap="round"
              />
            </svg>

            {/* Inner text inside circle */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center select-none">
              <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
                {state.isActive 
                  ? state.isFasting ? t.fastingStatus : t.eatingStatus
                  : t.inactiveStatus
                }
              </span>
              <span className="text-2xl font-black text-[var(--text)] font-mono tracking-tight my-0.5">
                {state.isActive 
                  ? isOverTime && state.isFasting
                    ? formattedElapsed 
                    : formattedRemaining
                  : "00:00:00"
                }
              </span>
              {state.isActive && (
                <span className={cn(
                  "text-[10px] font-bold px-2 py-0.5 rounded-full font-sans",
                  isOverTime 
                    ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" 
                    : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500"
                )}>
                  {isOverTime ? t.completed : `${Math.round(progressPercentage)}%`}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Info & Controls - Spans 7 columns on desktop */}
        <div className="md:col-span-7 space-y-4">
          {/* Status and Current Protocol Info */}
          <div className="bg-[var(--input-bg)] rounded-2xl p-4 space-y-2.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-zinc-500 uppercase tracking-widest font-bold text-[9px]">{t.protocol}</span>
              <span className="text-[var(--text)] font-extrabold">{currentProtocolDetail.label}</span>
            </div>
            <p className="text-[11px] text-zinc-500 leading-normal">
              {lang.startsWith('ua') ? currentProtocolDetail.descUa : currentProtocolDetail.descEn}
            </p>

            {state.isActive && state.startTime > 0 && (
              <div className="border-t border-[var(--card-border)] pt-2.5 grid grid-cols-2 gap-2 text-center">
                <div>
                  <div className="text-[8px] font-black text-zinc-400 uppercase tracking-widest">{t.elapsed}</div>
                  <div className="text-xs font-black text-[var(--text)] font-mono mt-0.5">{formattedElapsed}</div>
                </div>
                <div>
                  <div className="text-[8px] font-black text-zinc-400 uppercase tracking-widest">
                    {isOverTime && state.isFasting ? t.extraTime : t.remaining}
                  </div>
                  <div className={cn(
                    "text-xs font-black font-mono mt-0.5",
                    isOverTime && state.isFasting ? "text-emerald-500 animate-pulse" : "text-[var(--text)]"
                  )}>
                    {isOverTime && state.isFasting ? `+${formattedOvertime}` : formattedRemaining}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            {!state.isActive ? (
              <button
                onClick={handleStartFasting}
                className="flex-1 min-w-[150px] bg-emerald-500 hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-700 text-white font-bold text-xs uppercase tracking-widest py-3 px-4 rounded-2xl shadow-lg shadow-emerald-500/10 active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <Play className="w-4 h-4 fill-current" />
                {t.startFast}
              </button>
            ) : (
              <>
                {state.isFasting ? (
                  <button
                    onClick={handleStartEating}
                    className="flex-1 min-w-[150px] bg-emerald-500 hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-700 text-white font-bold text-xs uppercase tracking-widest py-3 px-4 rounded-2xl shadow-lg shadow-emerald-500/10 active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <CheckCircle className="w-4 h-4" />
                    {t.endFast}
                  </button>
                ) : (
                  <button
                    onClick={handleEndEatingAndStartFast}
                    className="flex-1 min-w-[150px] bg-emerald-500 hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-700 text-white font-bold text-xs uppercase tracking-widest py-3 px-4 rounded-2xl shadow-lg shadow-emerald-500/10 active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Play className="w-4 h-4" />
                    {t.endEating}
                  </button>
                )}

                <button
                  onClick={handleResetTimer}
                  className="p-3 bg-[var(--input-bg)] text-red-500 hover:bg-red-500/10 rounded-2xl transition-all hover:scale-105 active:scale-95 flex items-center justify-center cursor-pointer border border-red-500/10"
                  title={t.resetTimer}
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </>
            )}
          </div>

          {/* Quick Adjustment Buttons when Active */}
          {state.isActive && state.startTime > 0 && (
            <div className="space-y-1.5 pt-1">
              <span className="text-[9px] font-black uppercase tracking-widest text-zinc-400 block">{t.adjustStart}</span>
              <div className="grid grid-cols-4 gap-2">
                <button
                  onClick={() => handleAdjustTime(-30)}
                  className="py-1.5 rounded-xl bg-[var(--input-bg)] text-[10px] font-bold text-zinc-500 hover:text-[var(--text)] transition-all hover:scale-105 active:scale-95"
                >
                  {t.back30m}
                </button>
                <button
                  onClick={() => handleAdjustTime(-60)}
                  className="py-1.5 rounded-xl bg-[var(--input-bg)] text-[10px] font-bold text-zinc-500 hover:text-[var(--text)] transition-all hover:scale-105 active:scale-95"
                >
                  {t.back1h}
                </button>
                <button
                  onClick={() => handleAdjustTime(30)}
                  className="py-1.5 rounded-xl bg-[var(--input-bg)] text-[10px] font-bold text-zinc-500 hover:text-[var(--text)] transition-all hover:scale-105 active:scale-95"
                >
                  {t.forward30m}
                </button>
                <button
                  onClick={() => handleAdjustTime(60)}
                  className="py-1.5 rounded-xl bg-[var(--input-bg)] text-[10px] font-bold text-zinc-500 hover:text-[var(--text)] transition-all hover:scale-105 active:scale-95"
                >
                  {t.forward1h}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Protocol Settings Panel */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden mt-4 pt-4 border-t border-[var(--card-border)] relative z-10"
          >
            <h4 className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-3">{t.protocol}</h4>
            <div className="grid grid-cols-2 gap-2.5">
              {PROTOCOLS.map((proto) => {
                const isSelected = state.protocol === proto.value;
                return (
                  <button
                    key={proto.value}
                    onClick={() => handleSelectProtocol(proto.value)}
                    className={cn(
                      "p-3 rounded-2xl border text-left transition-all active:scale-95 cursor-pointer flex flex-col justify-center",
                      isSelected 
                        ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-500 dark:text-emerald-400" 
                        : "bg-[var(--input-bg)] border-[var(--card-border)] text-zinc-500 hover:text-[var(--text)]"
                    )}
                  >
                    <span className="text-xs font-black tracking-tight">{proto.label}</span>
                    <span className="text-[9px] mt-1 opacity-80 leading-tight">
                      {lang.startsWith('ua') ? proto.descUa : proto.descEn}
                    </span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* History Panel */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden mt-4 pt-4 border-t border-[var(--card-border)] relative z-10"
          >
            <h4 className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-3">{t.fastingLogs}</h4>
            
            {state.history.length === 0 ? (
              <p className="text-xs text-zinc-400 italic py-2 text-center">{t.noLogs}</p>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1 no-scrollbar">
                {state.history.map((log) => {
                  const durationFormatted = `${Math.floor(log.actualDuration)}h ${Math.round((log.actualDuration % 1) * 60)}m`;
                  const logDate = new Date(log.endTime).toLocaleDateString(lang.startsWith('ua') ? 'uk-UA' : 'en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  });

                  return (
                    <div
                      key={log.id}
                      className="bg-[var(--input-bg)] border border-[var(--card-border)]/60 rounded-2xl p-3 flex items-center justify-between text-xs gap-3"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className={cn(
                          "p-1.5 rounded-xl border",
                          log.isFastingPhase 
                            ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" 
                            : "bg-blue-500/10 text-blue-500 border-blue-500/20"
                        )}>
                          <Clock className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-extrabold text-[var(--text)] tracking-tight">
                            {log.isFastingPhase ? t.fastingStatus : t.eatingStatus} ({log.protocol})
                          </div>
                          <div className="text-[10px] text-zinc-400 mt-0.5">{logDate}</div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3.5 shrink-0">
                        <div className="text-right">
                          <div className="font-mono font-bold text-[var(--text)]">{durationFormatted}</div>
                          <div className="text-[9px] text-zinc-400 uppercase tracking-widest">
                            {t.target}: {log.targetDuration}h
                          </div>
                        </div>

                        {/* Delete Log Button */}
                        <button
                          onClick={() => handleDeleteHistoryLog(log.id)}
                          className="p-1.5 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-500/10 transition-colors cursor-pointer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
